import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { useSocket } from 'sonata-platform-components';
import { showNotification } from 'sonata-react-components';

import { getProcessResults, processEntityFile, updateProcessStatus } from './multiFlightMap.resources';

import type {
  MultiFlightEntityType,
  WSFileProcessErrorMessage,
  WSFileProcessMessage
} from 'sonata-static-storage/lib/common/common.types';
import type { ProcessUploadZone } from 'sonata-static-storage/lib/schemas/processUpload/processUploadZone.schema';

const CURRENT_KEY: Record<string, any> = {
  venue: 'venues',
  zone: 'locations'
};

export type ProcessStatus = 'idle' | 'processing' | 'success' | 'partial-success' | 'error' | 'timeout-error';

const UPLOAD_TIMEOUT_MS = 300_000;

export function useProcessCSVFile({
  entityId,
  entityType,
  onAddLocations,
  onAddVenues
}: {
  entityId: string;
  entityType: MultiFlightEntityType;
  onAddLocations: (batchZones: ProcessUploadZone[]) => Promise<void>;
  onAddVenues: (sources: Record<string, any>[]) => void;
}) {
  const componentId = 'ProcessLocationsId';
  const [status, setStatus] = useState<ProcessStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processIdRef = useRef<string>('');
  const { offEvent, onEvent } = useSocket();
  const { formatMessage } = useIntl();

  const processTokens: Record<string, any> = {
    locations: {},
    venues: {}
  };
  const resultsToken: Record<string, any> = {
    locations: {},
    venues: {}
  };
  const updateStatusTokens: Record<string, any> = {
    locations: {},
    venues: {}
  };

  const clearUploadTimeout = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const showProcessFinishNotification = (type: 'zone' | 'venue', processStatus: ProcessStatus) => {
    const notificationEntityType = type === 'zone' ? 'Locations' : 'Venues';
    let defaultMessage = '{entityType} file items successfully added';
    if (processStatus === 'partial-success') {
      defaultMessage = '{entityType} file items added, but there were some items unable to be processed';
    } else if (processStatus === 'error') {
      defaultMessage = "{entityType} file items couldn't be processed, please try again";
    } else if (processStatus === 'timeout-error') {
      defaultMessage = '{entityType} file processing timed out, please try again';
    }
    const formattedProcessStatus = processStatus.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
    const isErrorLike = processStatus === 'error' || processStatus === 'timeout-error';
    showNotification(
      isErrorLike ? 'error' : 'map',
      formatMessage(
        {
          defaultMessage,
          id: `entityFileUpload.results.${formattedProcessStatus}`
        },
        {
          entityType: notificationEntityType
        }
      )
    );
  };

  const handleWsErrorMessage = async (processId: string, msg: WSFileProcessErrorMessage, type: 'zone' | 'venue') => {
    try {
      if (msg.processId === processId) {
        clearUploadTimeout();
        console.error(`${processId} - Error processing ${type} file: ${msg.errorMsg}`);
        showProcessFinishNotification(type, 'error');
        setStatus('error');
      }
    } catch (_err) {
      clearUploadTimeout();
      setStatus('error');
    }
  };

  const handleWsMessage = async (processId: string, msg: WSFileProcessMessage, type: 'zone' | 'venue') => {
    try {
      if (msg.processId === processId) {
        clearUploadTimeout();
        const { items } = await getProcessResults(processId, resultsToken[CURRENT_KEY[type]]);
        if (type === 'zone') {
          onAddLocations(items);
        }
        if (type === 'venue') {
          onAddVenues(items);
        }
        await updateProcessStatus(processId, 'accept', updateStatusTokens[CURRENT_KEY[type]]);
        const updatedStatus = items.some(({ status: itemStatus }: { status: boolean }) => !itemStatus)
          ? 'partial-success'
          : 'success';
        setStatus(updatedStatus);
        showProcessFinishNotification(type, updatedStatus);
      }
    } catch (_err) {
      clearUploadTimeout();
      setStatus('error');
    }
  };

  const processBatchUploadFile = async (
    type: 'zone' | 'venue',
    fileData: {
      columnDelimiter?: string;
      country?: string;
      decimalDelimiter?: string;
      fileName: string;
      keysToReview: { fileCol: string; key: string }[];
      lineDelimiter?: string;
      totalLines?: number;
    }
  ) => {
    try {
      setStatus('processing');
      let processId = '';

      clearUploadTimeout();
      timeoutRef.current = setTimeout(() => {
        offEvent(componentId);
        setStatus('timeout-error');
        showProcessFinishNotification(type, 'timeout-error');
      }, UPLOAD_TIMEOUT_MS);

      onEvent(componentId, 'venueMapUploadProcessSuccess', (message: WSFileProcessMessage) => {
        handleWsMessage(processId, message, type);
      });
      onEvent(componentId, 'venueMapUploadProcessError', (message: WSFileProcessErrorMessage) => {
        handleWsErrorMessage(processId, message, type);
      });

      ({ processId = '' } = await processEntityFile(
        entityType,
        entityId,
        type === 'zone' ? 'zones' : 'venues',
        fileData,
        'venueMapUpload',
        null,
        false,
        processTokens[CURRENT_KEY[type]]
      ));
      processIdRef.current = processId;
    } catch (_e) {
      clearUploadTimeout();
      setStatus('error');
    }
  };

  const cancelBatchUploadFile = async () => {
    if (!processIdRef.current) {
      return;
    }
    clearUploadTimeout();
    offEvent(componentId);
    await updateProcessStatus(processIdRef.current, 'cancelled', updateStatusTokens[CURRENT_KEY.zone]);
    processIdRef.current = '';
    setStatus('idle');
  };

  useEffect(() => {
    return () => {
      clearUploadTimeout();
      processTokens[CURRENT_KEY.venue]?.cancel?.();
      processTokens[CURRENT_KEY.zone]?.cancel?.();
      offEvent(componentId);
    };
  }, []);

  return {
    cancelBatchUploadFile,
    processBatchUploadFile,
    processBatchUploadFileStatus: status
  };
}

// Registers a pre-coloured image on the map and re-registers it when a style switch drops it
// (styleimagemissing). The SVG is rasterised at the device pixel ratio and at the largest
// icon-size it is drawn with (maxIconSize), so it stays sharp; the logical size stays
// width x height, so icon-size values are relative to it. (sdf: true on a plain raster
// gives jagged edges, it expects a real distance field.)
export function useMapImage({
  color,
  height,
  imageId,
  mapId,
  maxIconSize,
  url,
  width
}: {
  color: string;
  height: number;
  imageId: string;
  mapId: string;
  maxIconSize: number;
  url: string;
  width: number;
}) {
  const { [mapId]: map } = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const mapInstance = map.getMap();
    const pixelRatio = maxIconSize * window.devicePixelRatio;
    const canvasWidth = Math.ceil(width * pixelRatio);
    const canvasHeight = Math.ceil(height * pixelRatio);

    const registerImage = () => {
      if (mapInstance.hasImage(imageId)) {
        return;
      }
      const svgImage = new Image(canvasWidth, canvasHeight);
      svgImage.onload = () => {
        const context = document.createElement('canvas').getContext('2d');
        if (!context || mapInstance.hasImage(imageId)) {
          return;
        }
        context.canvas.width = canvasWidth;
        context.canvas.height = canvasHeight;
        context.drawImage(svgImage, 0, 0, canvasWidth, canvasHeight);
        context.globalCompositeOperation = 'source-in';
        context.fillStyle = color;
        context.fillRect(0, 0, canvasWidth, canvasHeight);
        mapInstance.addImage(imageId, context.getImageData(0, 0, canvasWidth, canvasHeight), { pixelRatio });
      };
      svgImage.src = url;
    };

    registerImage();

    const handleStyleImageMissing = (event: { id: string }) => {
      if (event.id === imageId) {
        registerImage();
      }
    };

    mapInstance.on('styleimagemissing', handleStyleImageMissing);

    return () => {
      mapInstance.off('styleimagemissing', handleStyleImageMissing);
    };
  }, [map, color, height, imageId, maxIconSize, url, width]);
}
